import React, { createContext, useState, useEffect, useCallback } from "react";
import Overlay from "./components/overlay";
import Header from "./components/header";
import ProductsList from "./components/productsList";
import Form from "./components/forms/form";
import DiscountInput from "./components/inputs/discountInput";
import OrderTotals from "./components/OrderTotals";
import BenefitsListV2 from "./components/BenefitsListV2";
import TestimonialsList from "./components/TestimonialsList";
import SimpleFooter from "./components/simpleFooter";
import OrderOverviewDrawer from "./components/mobileComponents/OrderOverviewDrawer";
import MobileSectionSeperator from "./components/mobileComponents/MobileSectionSeperator";
import UpsellPopupOverlay from "./components/upsellPopupOverlay.jsx";
import VariantsPickerSeparate from "./components/ProductPackages/VariantsPickerSeparate";
import Separator from "./components/Separator";
import Bars from "./components/Bars";
import ProductPackages from "./components/ProductPackages/ProductPackages";
import OrderOverviewDrawerV3 from "./components/OrderOverviewDrawerV3";
import ProductHeroSection from "./components/ProductHeroContainer/ProductHeroSection";
import TopbarOffer from "./components/TopbarOffer";
import ExtendedWarrantyCheckbox from "./components/ExtendedWarrantyCheckbox";
import UpsellPopup from "./components/UpsellPopup.jsx";
import "./style.css";

// variables

const base = window.post_template;
const complianceMode =
  (window.isMobile == false && window.msg?.enable_compliance_version_in_desktop) ||
  (window.isMobile == true && window.msg?.enable_compliance_version_in_mobile);
const content = window.post_template.checkout_v4_meta_bars;
const onlyLabel = content.tr_checkout_v4_meta_bars_only_label;
const itemLabel = content.tr_checkout_v4_meta_bars_itemsLabel;
const enableVariants = window?.productData?.enable_color_variants || window?.productData?.enable_size_variants;
const newPicker = window?.productData?.show_product_picker_v2;

// checkout context declaration
const TemplateContext = createContext(null);

const Layout = () => {
  const [templateContext, setTemplateContext] = useState({});
  const [productPackage, setProductPackage] = useState(0);
  const [variantsContext, setVariantsContext] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1071);
  const [mobileGallery, setMobileGallery] = useState(window.innerWidth <= 767);

  useEffect(() => {
    function updateIsMobile() {
      setIsMobile(window.innerWidth <= 767);
      setMobileGallery(window.innerWidth < 1072);
    }

    window.addEventListener("resize", updateIsMobile);
    updateIsMobile();
    return () => {
      window.removeEventListener("resize", updateIsMobile);
    };
  }, []);

  useEffect(() => {
    const primaryProduct = window.products?.filter((item) => {
      return item.best_seller == true;
    });

    let defaultColor;

    if (window.productData?.enable_color_variants) {
      defaultColor = window.productData?.product_color_variants?.find((variant) => {
        return variant.default_variant == true;
      });
    }

    let defaultSize;

    if (window.productData?.enable_size_variants) {
      defaultSize = window.productData?.product_size_variants?.find((variant) => {
        return variant.default_variant == true;
      });
    }

    let paypalHandler = "standart";

    if (window.productData?.paypal_merchant) {
      const paypalMerchant = window.productData.paypal_merchant.split(",");
      if (paypalMerchant[0] !== null) {
        paypalHandler = paypalMerchant[0];
      }
    }

    let defaultVariant = {};

    if (defaultColor) {
      defaultVariant = {
        ...defaultVariant,
        color: defaultColor?.value,
        colorLabel: defaultColor.tr_label,
      };
    }

    if (defaultSize) {
      defaultVariant = {
        ...defaultVariant,
        size: defaultSize?.value,
        sizeLabel: defaultSize.tr_label,
      };
    }

    if (defaultVariant && window?.productData?.variants_picker_type == "each") {
      const maxSize = window?.products?.reduce((max, product) => {
        return Number(product.amount) > max ? Number(product.amount) : max;
      }, 0);
      setVariantsContext({
        defaultVariant: defaultVariant,
        variants: [...Array.from({ length: maxSize })].map(function (object, i) {
          return defaultVariant;
        }),
      });
    }

    if (defaultVariant && window?.productData?.variants_picker_type == "separate") {
      const maxSize = window?.products?.reduce((max, product) => {
        return Number(product.amount) > max ? Number(product.amount) : max;
      }, 0);

      setVariantsContext({
        defaultVariant: defaultVariant,
        variants: [...Array.from({ length: maxSize })].map(function (object, i) {
          return defaultVariant;
        }),
      });
    }

    setTemplateContext({
      config: {
        product: {
          ...primaryProduct[0],
        },
        campaignId: window.productData?.konnektive_campaign_id,
        nextStep: window.productData?.next_page_on_success,
        warrantyState:
          window.productData.add_warranty_for_product == false ? false : window.productData?.preselect_warranty,
        isWarrantySelected: window.productData.add_warranty_for_product,
        warrantyTexts: window.productData?.warranty_texts,
      },
      showPopup: window.productData?.show_popover,
      popupClicked: false,
      upsellPopupClicked: false,
      displayPopup: false,
      paymentMethod: "card",
      useUpsell: base?.checkout_v4_products.checkout_v4_upsell_popup_toggle,
      displayUpsellPopup: false,
      // exception only applicable to JPY currency
      priceWithCents: window.productData.currency_code == "JPY" ? false : true,
      isLoading: false,
      discountsEnabled: !!window.productData.enable_discount_codes,
      discountCode: undefined,
      isDiscountApplied: templateContext.discountCode === window.productData?.discount_codes?.discount_code,
      orderOverviewDrawerOpen: base?.checkout_v4_summary.checkout_v4_summary_state,
      withTopbarOffer: base.checkout_v4_topbar?.checkout_v4_topbar_show,
      withProductHeroSection: base.checkout_v4_product_hero.checkout_v4_product_hero_show,
      withMetaBars: base.checkout_v4_meta_bars.checkout_v4_meta_bars_show,
      withOrderOverviewV2: base.checkout_v4_summary.checkout_v4_summary_type == "dropdown" ? true : false,
      withProductPicker: base.checkout_v4_products.checkout_v4_products_show,
      withDividedForm: base.checkout_v4_form.checkout_V4_form_emailSeperate,
      paypalHandler: paypalHandler,
      totalSavingsStyle: base.checkout_v4_summary.checkout_v4_summary_savingsStyl,
      paypalButtonState: "initial",
      complianceMode: complianceMode,
      showBulletPoints: base?.checkout_v4_summary.checkout_v4_summary_bullet_points ?? false,
    });
  }, []);

  const isDefaultHeroLayout =
    window?.post_template?.checkout_v4_product_hero?.checkout_v4_product_hero_variant == "default";

  const handleCancelUpsell = useCallback(() => {
    setTemplateContext((prevContext) => ({
      ...prevContext,
      upsellPopupClicked: true,
      displayUpsellPopup: false,
    }));
  }, []);

  useEffect(() => {
    if (templateContext.displayUpsellPopup) {
      document.body.style.setProperty("overflow", "hidden", "important");
    } else {
      document.body.style.removeProperty("overflow");
    }

    return () => {
      document.body.style.removeProperty("overflow");
    };
  }, [templateContext.displayUpsellPopup]);

  return (
    <TemplateContext.Provider value={[templateContext, setTemplateContext, variantsContext, setVariantsContext]}>
      {templateContext.withTopbarOffer && <TopbarOffer />}
      <Header />
      {templateContext.withProductHeroSection && <ProductHeroSection isMobile={mobileGallery} />}
      <main className="main" data-cy="cc-v4">
        <div className="main_leftSide"></div>
        {isMobile && !templateContext.withOrderOverviewV2 && <OrderOverviewDrawer />}
        <section className="contentContainer">
          <article
            className={`productContainer
                     ${templateContext.withMetaBars || templateContext.withProductPicker ? "v2" : ""}
                     ${!templateContext.withOrderOverviewV2 && ""}`}
          >
            {!newPicker
              ? !isMobile && templateContext.withMetaBars && <Bars />
              : templateContext.withMetaBars && <Bars />}
            {(templateContext.withMetaBars || templateContext.withProductHeroSection) &&
            templateContext.withProductPicker &&
            templateContext.withOrderOverviewV2 &&
            !isDefaultHeroLayout ? (
              <MobileSectionSeperator border="" margin="0px 16px 0px -16px" />
            ) : (
              ""
            )}
            {window?.isMobile &&
              window?.post_template.checkout_v4_product_hero.checkout_v4_product_hero_variant == "oneBar" && (
                <Bars mobile={true} onlyProgress={true}></Bars>
              )}
            {window?.productData?.variants_picker_type == "separate" && enableVariants && (
              <>
                <VariantsPickerSeparate />
                <MobileSectionSeperator margin="0px 0px 0px -16px" />
              </>
            )}
            {templateContext.withProductPicker && (
              <ProductPackages productPackage={productPackage} setProductPackage={setProductPackage} />
            )}
            {templateContext.withMetaBars || templateContext.withProductHeroSection ? (
              <MobileSectionSeperator margin="0px 0px 24px -16px" />
            ) : (
              ""
            )}
            <Form withDividedForm={templateContext.withDividedForm} />
            {!isMobile && <SimpleFooter />}
          </article>
          {/* {isMobile && !templateContext.withOrderOverviewV2 && <OrderOverviewDrawerV2 />} */}
          <div className={`summaryContainer ${templateContext.withOrderOverviewV2 && "v2"}`}>
            {!isMobile && !templateContext.withOrderOverviewV2 && (
              <>
                <ProductsList />
                <DiscountInput />
                <Separator margin="16px 0px 16px 0px" />
                <ExtendedWarrantyCheckbox />
                <Separator />
                <OrderTotals />
              </>
            )}
            {templateContext.withOrderOverviewV2 && <OrderOverviewDrawerV3 />}
            <BenefitsListV2 />
            {!templateContext.complianceMode && <TestimonialsList />}
          </div>
          {isMobile && <SimpleFooter />}
        </section>
        <div className="main_rightSide"></div>
      </main>

      {((templateContext.displayPopup && !templateContext.popupClicked) || templateContext.isLoading) && (
        <Overlay loading={templateContext?.isLoading} paymentMethod={templateContext?.paymentMethod} />
      )}
      {templateContext.displayUpsellPopup && (
        <UpsellPopupOverlay>
          <UpsellPopup onCancel={handleCancelUpsell} />
        </UpsellPopupOverlay>
      )}
    </TemplateContext.Provider>
  );
};

export { Layout, TemplateContext };
